import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { DashboardModule } from '@/config/dashboardModules';

interface ModuleCardProps {
  module: DashboardModule;
  onActivate: () => void;
  active?: boolean;
}

/** How many bullets to show in compact (collapsed) state. */
const COMPACT_BULLETS = 2;
/** Max chars for compact description before truncation. */
const COMPACT_DESC_CHARS = 70;

/**
 * Resolve a color stem (e.g. 'sky', 'emerald', 'primary', 'accent') into the
 * Tailwind classes used across all module cards. Includes a static safelist so
 * Tailwind's JIT picks up every variant at build time.
 */
const COLOR_CLASSES: Record<
  string,
  { border: string; borderHover: string; bg: string; text: string; bullet: string; ringActive: string }
> = {
  primary: {
    border: 'border-primary/10', borderHover: 'hover:border-primary/30',
    bg: 'bg-primary/10', text: 'text-primary', bullet: 'text-primary',
    ringActive: 'border-primary ring-2 ring-primary/50 shadow-lg shadow-primary/20 bg-primary/5',
  },
  accent: {
    border: 'border-accent/10', borderHover: 'hover:border-accent/30',
    bg: 'bg-accent/10', text: 'text-accent', bullet: 'text-accent',
    ringActive: 'border-accent ring-2 ring-accent/50 shadow-lg shadow-accent/20 bg-accent/5',
  },
  red: {
    border: 'border-red-500/10', borderHover: 'hover:border-red-500/30',
    bg: 'bg-red-500/10', text: 'text-red-500', bullet: 'text-red-500',
    ringActive: 'border-red-500 ring-2 ring-red-500/50 shadow-lg shadow-red-500/20 bg-red-500/5',
  },
  orange: {
    border: 'border-orange-500/10', borderHover: 'hover:border-orange-500/30',
    bg: 'bg-orange-500/10', text: 'text-orange-600 dark:text-orange-400', bullet: 'text-orange-500',
    ringActive: 'border-orange-500 ring-2 ring-orange-500/50 shadow-lg shadow-orange-500/20 bg-orange-500/5',
  },
  purple: {
    border: 'border-purple-500/10', borderHover: 'hover:border-purple-500/30',
    bg: 'bg-purple-500/10', text: 'text-purple-600 dark:text-purple-400', bullet: 'text-purple-500',
    ringActive: 'border-purple-500 ring-2 ring-purple-500/50 shadow-lg shadow-purple-500/20 bg-purple-500/5',
  },
  cyan: {
    border: 'border-cyan-500/10', borderHover: 'hover:border-cyan-500/30',
    bg: 'bg-cyan-500/10', text: 'text-cyan-600 dark:text-cyan-400', bullet: 'text-cyan-500',
    ringActive: 'border-cyan-500 ring-2 ring-cyan-500/50 shadow-lg shadow-cyan-500/20 bg-cyan-500/5',
  },
  teal: {
    border: 'border-teal-500/10', borderHover: 'hover:border-teal-500/30',
    bg: 'bg-teal-500/10', text: 'text-teal-600 dark:text-teal-400', bullet: 'text-teal-500',
    ringActive: 'border-teal-500 ring-2 ring-teal-500/50 shadow-lg shadow-teal-500/20 bg-teal-500/5',
  },
  emerald: {
    border: 'border-emerald-500/10', borderHover: 'hover:border-emerald-500/30',
    bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', bullet: 'text-emerald-500',
    ringActive: 'border-emerald-500 ring-2 ring-emerald-500/50 shadow-lg shadow-emerald-500/20 bg-emerald-500/5',
  },
  amber: {
    border: 'border-amber-500/10', borderHover: 'hover:border-amber-500/30',
    bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400', bullet: 'text-amber-500',
    ringActive: 'border-amber-500 ring-2 ring-amber-500/50 shadow-lg shadow-amber-500/20 bg-amber-500/5',
  },
  blue: {
    border: 'border-blue-500/10', borderHover: 'hover:border-blue-500/30',
    bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', bullet: 'text-blue-500',
    ringActive: 'border-blue-500 ring-2 ring-blue-500/50 shadow-lg shadow-blue-500/20 bg-blue-500/5',
  },
  rose: {
    border: 'border-rose-500/10', borderHover: 'hover:border-rose-500/30',
    bg: 'bg-rose-500/10', text: 'text-rose-600 dark:text-rose-400', bullet: 'text-rose-500',
    ringActive: 'border-rose-500 ring-2 ring-rose-500/50 shadow-lg shadow-rose-500/20 bg-rose-500/5',
  },
  indigo: {
    border: 'border-indigo-500/10', borderHover: 'hover:border-indigo-500/30',
    bg: 'bg-indigo-500/10', text: 'text-indigo-600 dark:text-indigo-400', bullet: 'text-indigo-500',
    ringActive: 'border-indigo-500 ring-2 ring-indigo-500/50 shadow-lg shadow-indigo-500/20 bg-indigo-500/5',
  },
  sky: {
    border: 'border-sky-500/10', borderHover: 'hover:border-sky-500/30',
    bg: 'bg-sky-500/10', text: 'text-sky-600 dark:text-sky-400', bullet: 'text-sky-500',
    ringActive: 'border-sky-500 ring-2 ring-sky-500/50 shadow-lg shadow-sky-500/20 bg-sky-500/5',
  },
  violet: {
    border: 'border-violet-500/10', borderHover: 'hover:border-violet-500/30',
    bg: 'bg-violet-500/10', text: 'text-violet-600 dark:text-violet-400', bullet: 'text-violet-500',
    ringActive: 'border-violet-500 ring-2 ring-violet-500/50 shadow-lg shadow-violet-500/20 bg-violet-500/5',
  },
  fuchsia: {
    border: 'border-fuchsia-500/10', borderHover: 'hover:border-fuchsia-500/30',
    bg: 'bg-fuchsia-500/10', text: 'text-fuchsia-600 dark:text-fuchsia-400', bullet: 'text-fuchsia-500',
    ringActive: 'border-fuchsia-500 ring-2 ring-fuchsia-500/50 shadow-lg shadow-fuchsia-500/20 bg-fuchsia-500/5',
  },
};

export function DashboardModuleCard({ module, onActivate, active }: ModuleCardProps) {
  const Icon = module.icon;
  const c = COLOR_CLASSES[module.color] ?? COLOR_CLASSES.primary;

  // pinned = explicit user toggle; hovered = transient expand on hover/focus
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const expanded = pinned || hovered;

  const totalBullets = module.bullets.length;
  const hasMoreBullets = totalBullets > COMPACT_BULLETS;
  const visibleBullets = expanded ? module.bullets : module.bullets.slice(0, COMPACT_BULLETS);

  const descNeedsTruncate = module.description.length > COMPACT_DESC_CHARS;
  const shortDesc = descNeedsTruncate
    ? module.description.slice(0, COMPACT_DESC_CHARS).trimEnd() + '…'
    : module.description;

  const togglePinned = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setPinned((v) => !v);
  };

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onActivate}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onActivate();
        }
      }}
      className={cn(
        'group relative cursor-pointer bg-card/80 backdrop-blur-sm transition-all duration-300',
        'hover:shadow-lg hover:scale-[1.02]',
        'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        c.border,
        c.borderHover,
        active && c.ringActive,
        expanded && 'shadow-lg z-10'
      )}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start gap-3">
          <div className={cn('p-2.5 rounded-xl shrink-0', c.bg)}>
            <Icon className={cn('h-5 w-5', c.text)} />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base">{module.title}</CardTitle>
            <CardDescription className="text-xs">
              {expanded ? module.description : shortDesc}
            </CardDescription>
          </div>
          {(hasMoreBullets || descNeedsTruncate) && (
            <button
              type="button"
              onClick={togglePinned}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') togglePinned(e);
              }}
              aria-label={pinned ? 'Collapse details' : 'Expand details'}
              aria-expanded={pinned}
              className={cn(
                'shrink-0 p-1 rounded-md text-muted-foreground hover:bg-muted/60 transition',
                pinned && c.text
              )}
            >
              {pinned ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="text-xs text-muted-foreground space-y-1.5 ml-1">
          {visibleBullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className={cn('mt-0.5', c.bullet)}>•</span>
              {typeof b === 'string' ? (
                <span>{b}</span>
              ) : (
                <span><b>{b.bold}</b> {b.text}</span>
              )}
            </li>
          ))}
        </ul>
        {!expanded && hasMoreBullets && (
          <button
            type="button"
            onClick={togglePinned}
            className={cn(
              'mt-2 text-[11px] font-medium opacity-70 hover:opacity-100 transition',
              c.text
            )}
          >
            +{totalBullets - COMPACT_BULLETS} more
          </button>
        )}
      </CardContent>
    </Card>
  );
}
